import React, { useState, useContext, useEffect, useCallback } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { SiReaddotcv } from "react-icons/si";
import { RiMailStarLine } from "react-icons/ri";
import { VscCopy } from "react-icons/vsc";
import { AiOutlineClose } from "react-icons/ai";
import { FaDownload, FaRegFilePdf } from "react-icons/fa";
import { AuthContext } from "../context/AuthContext";
import { toast } from "react-hot-toast";
import UpdatesThumbnail from "./UpdatesThumbnail";
import PdfComp from "../viewer/PdfComp";
import pdfjsWorker from "pdfjs-dist/build/pdf.worker.entry";
import { pdfjs } from "react-pdf";

pdfjs.GlobalWorkerOptions.workerSrc = pdfjsWorker;

const ContractCard = ({ contract, onClose }) => {
  const { authState } = useContext(AuthContext);
  const [loadingDocument, setLoadingDocument] = useState(false);
  const [documentUrl, setDocumentUrl] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("details");

  const fetchContractDocument = useCallback(async () => {
    if (!contract || !contract._id) return;
    setLoadingDocument(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/bulk/contracts/${contract._id}/document`,
        {
          headers: {
            "x-auth-token": authState.token,
          },
          responseType: "blob",
        }
      );
      const fileUrl = URL.createObjectURL(response.data);
      setDocumentUrl(fileUrl);
    } catch (error) {
      if (error.response) {
        toast.error(`Error: ${error.response.data.message}`);
      } else {
        toast.error("An unexpected error occurred ⛔⛔");
      }
    } finally {
      setLoadingDocument(false);
    }
  }, [authState.token, contract]);

  useEffect(() => {
    fetchContractDocument();
  }, [fetchContractDocument]);

  useEffect(() => {
    const fetchAttachments = async () => {
      if (!contract || !contract._id) return;
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/attachments/contracts/${contract._id}/attachments`,
          {
            headers: {
              "x-auth-token": authState.token,
            },
          }
        );
        setAttachments(response.data.attachments);
      } catch (error) {
        if (error.response) {
          toast.error(`Error: ${error.response.data.message}`);
        } else {
          toast.error("An unexpected error occurred ⛔⛔");
        }
      }
    };
    fetchAttachments();
  }, [authState.token, contract]);

  const handleCopyLink = () => {
    navigator.clipboard.writeText(documentUrl);
    toast.success("Link copied to clipboard!");
  };

  const handleMailLink = () => {
    const mailtoLink = `mailto:?subject=Contract PDF&body=Here is the link to the contract PDF: ${documentUrl}`;
    window.location.href = mailtoLink;
  };

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  const downloadAttachment = async (filePath) => {
    try {
      const token = authState.token;
      const cleanedFilePath = filePath
        .replace(/contract-attachments\\/, "")
        .replace(/\\/g, "/");
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/attachments/contracts/${contract._id}/attachments/${cleanedFilePath}`;

      const response = await axios.get(url, {
        headers: { "x-auth-token": token },
        responseType: "blob",
      });

      const blobUrl = URL.createObjectURL(response.data);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = filePath.split("/").pop();
      document.body.appendChild(a);
      a.click();
      a.remove();

      URL.revokeObjectURL(blobUrl);
      toast.success("Attachment downloaded successfully");
    } catch (error) {
      console.error(`Error: ${error.message}`);
      toast.error(
        error.response?.data?.message || "An unexpected error occurred ⛔⛔"
      );
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50 p-4"
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-white rounded-xl shadow-2xl max-w-4xl w-full h-[90vh] overflow-hidden flex flex-col"
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-700 p-6 text-white">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold">
              {contract ? contract["Contract Number"] : ""}
            </h2>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={onClose}
              className="text-white hover:text-red-200 transition-colors duration-200"
            >
              <AiOutlineClose size={24} />
            </motion.button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b">
          <button
            className={`flex-1 py-3 px-4 font-medium ${
              activeTab === "details"
                ? "text-blue-600 border-b-2 border-blue-600"
                : "text-gray-500"
            }`}
            onClick={() => setActiveTab("details")}
          >
            Details
          </button>
          <button
            className={`flex-1 py-3 px-4 font-medium ${
              activeTab === "attachments"
                ? "text-blue-600 border-b-2 border-blue-600"
                : "text-gray-500"
            }`}
            onClick={() => setActiveTab("attachments")}
          >
            Attachments
          </button>
        </div>

        {/* Content */}
        <div className="flex-grow overflow-y-auto p-6">
          {activeTab === "details" && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex justify-center items-start">
                {documentUrl ? (
                  <div className="w-full max-w-sm">
                    <UpdatesThumbnail pdfUrl={documentUrl} />
                  </div>
                ) : (
                  <button
                    onClick={fetchContractDocument}
                    className="w-full max-w-sm py-3 px-4 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors duration-200"
                    disabled={loadingDocument}
                  >
                    {loadingDocument ? "Loading PDF..." : "Load Full PDF"}
                  </button>
                )}
              </div>
              <div className="space-y-3">
                {contract && (
                  <>
                    <DetailItem label="Category" value={contract.category} />
                    <DetailItem label="Parties" value={contract.Parties} />
                    <DetailItem
                      label="Start Date"
                      value={new Date(contract.startDate).toDateString()}
                    />
                    <DetailItem
                      label="End Date"
                      value={new Date(contract.endDate).toDateString()}
                    />
                    <DetailItem label="Status" value={contract.status} />
                    <DetailItem
                      label="Section/Dept"
                      value={contract["Section Or Department"]}
                    />
                    <DetailItem
                      label="Description"
                      value={contract["Contract Description"]}
                    />
                    <div className="mt-4 pt-4 border-t border-gray-200 text-sm text-gray-600">
                      <p>
                        Added by:{" "}
                        <span className="font-semibold">
                          {contract.createdBy}
                        </span>
                      </p>
                      <p>
                        Added on:{" "}
                        <span className="font-semibold">
                          {new Date(contract.createdAt).toLocaleDateString(
                            "en-US",
                            { year: "numeric", month: "long", day: "numeric" }
                          )}
                        </span>
                      </p>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {activeTab === "attachments" && (
            <div className="space-y-4">
              <h3 className="text-xl font-semibold text-gray-800">
                Attachments
              </h3>
              {attachments.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                  {attachments.map((attachment) => (
                    <motion.div
                      key={attachment._id}
                      whileHover={{ scale: 1.05 }}
                      className="bg-gray-100 p-4 rounded-lg shadow-md"
                    >
                      <div className="flex items-center space-x-3">
                        <FaRegFilePdf className="text-red-500 text-2xl" />
                        <div className="flex-grow">
                          <p className="font-medium text-gray-800 truncate">
                            {attachment.fileName}
                          </p>
                        </div>
                        <motion.button
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          onClick={() =>
                            downloadAttachment(attachment.filePath)
                          }
                          className="text-blue-600 hover:text-blue-800"
                        >
                          <FaDownload />
                        </motion.button>
                      </div>
                    </motion.div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500">No attachments available.</p>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        {documentUrl && (
          <div className="bg-gray-100 p-4 flex justify-between items-center">
            <a
              href={documentUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-blue-600 text-white py-2 px-4 rounded-full hover:bg-blue-700 transition-colors duration-200"
            >
              Download
            </a>
            <div className="flex space-x-4">
              <ActionButton
                icon={<VscCopy />}
                onClick={handleCopyLink}
                label="Copy Link"
              />
              <ActionButton
                icon={<RiMailStarLine />}
                onClick={handleMailLink}
                label="Mail"
              />
              <ActionButton
                icon={<SiReaddotcv />}
                onClick={openModal}
                label="View"
              />
            </div>
          </div>
        )}
      </motion.div>

      {/* PDF Viewer Modal */}
      <AnimatePresence>
        {isModalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-75 p-2 sm:p-4"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-xl shadow-2xl w-full max-w-4xl h-[95vh] flex flex-col"
            >
              <div className="p-2 sm:p-4 border-b flex justify-between items-center">
                <h3 className="text-lg sm:text-xl font-semibold truncate">
                  Contract: {contract["Contract Number"]}
                </h3>
                <button
                  onClick={closeModal}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <AiOutlineClose size={24} />
                </button>
              </div>
              <div className="flex-grow overflow-hidden">
                <PdfComp documentUrl={documentUrl} />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

const DetailItem = ({ label, value }) => (
  <div>
    <span className="font-medium text-gray-700">{label}: </span>
    <span className="text-gray-600">{value}</span>
  </div>
);

const ActionButton = ({ icon, onClick, label }) => (
  <motion.button
    whileHover={{ scale: 1.05 }}
    whileTap={{ scale: 0.95 }}
    onClick={onClick}
    className="flex items-center space-x-1 text-gray-600 hover:text-blue-600"
  >
    {icon}
    <span>{label}</span>
  </motion.button>
);

export default ContractCard;
